import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ClearIcon from '@mui/icons-material/Clear'; // Ajout de l'icône pour effacer la recherche
import CloseIcon from '@mui/icons-material/Close'; // Ajout de l'importation de l'icône de fermeture
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchOutlined from '@mui/icons-material/SearchOutlined'; // Ajout de l'icône de recherche
import { Box, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, InputAdornment, List, ListItem, TextField, Typography, useTheme } from '@mui/material';
import DragIcon from 'assets/DragIcon';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';
import ButtonCustom from './ButtonCustom';
import ButtonIcon from './ButtonIcon';
import ButtonIconText from './ButtonIconText';

export interface Dimension {
  id: any;
  visible: boolean;
  title: string;
  isEditable?: boolean;
}

export interface DimensionSelectorProps {
  categories: Dimension[];
  onToggle: (updatedCategories: Dimension[]) => void;
  open: boolean;
  handleClose: () => void;
}

export const DimensionFilter = ({ categories, onToggle, open, handleClose }: DimensionSelectorProps) => {
  const [localCategories, setLocalCategories] = useState<Dimension[]>([]);
  const [editCategoryId, setEditCategoryId] = useState<any | null>(null);
  const [editTitle, setEditTitle] = useState<string>('');
  const [editMode, setEditMode] = useState(true);
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<any>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [availableSelected, setAvailableSelected] = useState(0);
  const theme = useTheme();


  const handleDialogClose = () => {
    handleClose();
    setTimeout(() => {
      const nonFixedCategories = categories.slice(3, -2);
      setLocalCategories(nonFixedCategories);
      setEditCategoryId(null);
      setEditTitle('');
      setEditMode(true);
      setSearchValue(''); // Réinitialiser la recherche à la fermeture
    }, 300);
  };

  useEffect(() => {
    const nonFixedCategories = categories.slice(3, -2);
    // Preserve the order provided by parent (graph/layout order)
    setLocalCategories(nonFixedCategories);
    if (categories.length - 5 < 20) {
      setAvailableSelected(categories.length - 5);
    }else{
      setAvailableSelected(20);
    }
  }, [categories]);

  const handleChange = (categoryId: any) => {
    const category = localCategories.find(cat => cat.id === categoryId);
    const currentVisibleCount = localCategories.filter(cat => cat.visible).length;

    if (!category?.visible && currentVisibleCount >=20) {
      enqueueSnackbar(t('REVISION_GRAPH_FIELDS_LIMIT'), {
        variant: 'error',
      });
      return;
    }

    const updatedCategories = localCategories.map(category =>
      category.id === categoryId
        ? { ...category, visible: !category.visible }
        : category
    );

    // Do not reorder here; keep the current order intact to match graph and grid
    setLocalCategories(updatedCategories);
  };

  const handleEditText = (categoryId: any, newTitle: string) => {
    setEditMode(true);
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
    }, 0);
    const updatedCategories = localCategories.map(category =>
      category.id === categoryId ? { ...category, title: newTitle } : category
    );
    setLocalCategories(updatedCategories);
    
  };

  const handleSave = () => {
    const hasEmptyTitles = localCategories.some(category => !category.title.trim());
    if (hasEmptyTitles) {
      enqueueSnackbar(t('LAYOUT_EMPTY_TITLE_ERROR'), {
        variant: 'error',
      });
      return;
    }
    const titles = localCategories.map(category => category.title);
    const hasDuplicates = titles.some((title, index) => titles.indexOf(title) !== index);

    if (hasDuplicates) {
      enqueueSnackbar(t('LAYOUT_DUPLICATE_TITLE_ERROR'), {
        variant: 'warning',
      });
    }

    setIsUpdating(true);

    const updatedCategories = [
      ...categories.slice(0, 3),
      ...localCategories,
      ...categories.slice(-2)
    ];

    onToggle(updatedCategories);

    setTimeout(() => {
      setIsUpdating(false);
      handleClose();
    }, 1000);
  };


  const handleAddColumn = () => {
    const currentVisibleCount = localCategories.filter(cat => cat.visible).length;

    if (currentVisibleCount >= 20) {
      enqueueSnackbar(t('REVISION_GRAPH_FIELDS_LIMIT'), {
        variant: 'error',
      });
      return;
    }

    const newCategory: Dimension = {
      id: `new_${Date.now()}`,
      visible: true,
      title: '',
      isEditable: true
    };

    setLocalCategories([newCategory, ...localCategories]);
    setEditCategoryId(newCategory.id);
    setEditTitle('');

    // Scroll to the top after a short delay to ensure the new item is rendered
    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollToItem(0, 'start');
      }
    }, 100);
  };

  const handleDeleteCategory = (categoryId: any) => {
    setLocalCategories(prevCategories => {
      const updatedCategories = prevCategories.filter(category => category.id !== categoryId);
      return updatedCategories;
    });
  };



  // Fonction pour gérer la recherche
  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
  };

  const filteredCategories = useMemo(() => 
    localCategories.filter(category =>
      category.title.toLowerCase().includes(searchValue.toLowerCase())
    ), [localCategories, searchValue]
  );

  // Séparer les dimensions visibles et non-visibles
  const visibleCategories = useMemo(() => 
    filteredCategories.filter(category => category.visible), 
    [filteredCategories]
  );
  
  const nonVisibleCategories = useMemo(() => 
    filteredCategories.filter(category => !category.visible), 
    [filteredCategories]
  );

  // Seuil pour décider d'utiliser la virtualisation ou non (seulement pour les non-visibles)
  const VIRTUALIZATION_THRESHOLD = 100;
  const useVirtualizationForNonVisible = nonVisibleCategories.length > VIRTUALIZATION_THRESHOLD;

  const onDragEnd = useCallback((result: any) => {
    if (!result.destination) return;
    setDraggingIndex(null);

    const sourceCategory = visibleCategories[result.source.index];
    const destinationIndex = result.destination.index;

    const sourceIndex = localCategories.findIndex(cat => cat.id === sourceCategory.id);
    
    const reorderedVisibleCategories = Array.from(visibleCategories);
    const [removed] = reorderedVisibleCategories.splice(result.source.index, 1);
    reorderedVisibleCategories.splice(destinationIndex, 0, removed);

    const newLocalCategories = [...localCategories];
    
    newLocalCategories.splice(sourceIndex, 1);
    
    if (destinationIndex === 0) {
      newLocalCategories.unshift(removed);
    } else {
      const destinationCategory = reorderedVisibleCategories[destinationIndex - 1];
      const insertIndex = newLocalCategories.findIndex(cat => cat.id === destinationCategory.id) + 1;
      newLocalCategories.splice(insertIndex, 0, removed);
    }

    setLocalCategories(newLocalCategories);
  }, [localCategories, visibleCategories]);


  const DimensionItem = useCallback(({ category, index, isDragging = false, style = {} }: {
    category: Dimension;
    index: number;
    isDragging?: boolean;
    style?: React.CSSProperties;
  }) => {
    return (
      <ListItem
        style={{
          ...style,
          display: 'flex',
          alignItems: "center",
          justifyContent: "space-between",
          border: isDragging ? '1px solid #D9D9D9' : 'none',
          borderBottom: '1px solid #D9D9D9',
          height: "58px",
          backgroundColor: "white",
          transition: 'box-shadow 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}
          onMouseEnter={() => setHoveredId(category.id)}
          onMouseLeave={() => setHoveredId(null)}
        >
          <DragIcon />
          <Checkbox
            sx={{ p: "5px" }}
            color='secondary'
            checked={category.visible}
            onChange={() => handleChange(category.id)}
          />
          <Box sx={{ height: '58px', display: 'flex', alignItems: 'center', flex: 1 }}>
            {(hoveredId === category.id || category.id === editCategoryId) ? (
              <TextField
                inputRef={category.id === editCategoryId ? textFieldRef : null}
                value={category.title}
                onChange={(e) => handleEditText(category.id, e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '40px',
                    width: '96%',
                  },
                  '& .MuiOutlinedInput-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    paddingRight: '14px',
                    paddingLeft: '14px',
                  }
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: 16,
                  flexGrow: 1,
                  padding: '8px 14px',
                  cursor: 'pointer',
                  lineHeight: '40px'
                }}
              >
                {category.title}
              </Typography>
            )}
          </Box>
        </div>
        <div style={{ width: '48px', height: '48px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {typeof category.id === 'string' && category.id.startsWith('new_') && (
            <ButtonIcon
              onClick={() => handleDeleteCategory(category.id)}
            >
              <DeleteOutlineIcon />
            </ButtonIcon>
          )}
        </div>
      </ListItem>
    );
  }, [hoveredId, editCategoryId, handleChange, handleEditText, handleDeleteCategory, textFieldRef]);

  return (
    <>
      <Dialog onClose={handleDialogClose} open={open} fullWidth={true} sx={{
        '& .MuiDialog-paper': {
          boxShadow: 'none',
          height: "700px",
          backgroundColor: "white",
          minWidth: "600px",
          width: "90%",
          maxWidth: "800px",
        },
      }}>
        <Box sx={{
          minHeight: '91px',
          display: 'flex',
          position: 'relative',
          alignItems: "center",
          padding: "0 20px"
        }}>
          <Typography variant="h2" sx={{
            textAlign: 'left'
          }}>
            {t("REVISION_GRAPH_MODIFY_DIMENSION")}
          </Typography>
          <Box sx={{
            position: 'absolute',
            left: '52%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
          }}>
            <Box sx={{ display: 'flex', gap: '32px' }}>
              {/* Bouton d'ajout */}
              <ButtonIconText
                icon={<div style={{ fontSize: "40px", display: "flex", alignItems: "center" }}><AddRoundedIcon fontSize='inherit' /></div>}
                color='primary'
                disableElevation={true}
                type='submit'
                variant='contained'
                sx={{ minWidth: '176px', }}
                onClick={handleAddColumn}
              >
                {t("REVISION_GRAPH_ADD_DIMENSION")}
              </ButtonIconText>
              
              {/* Champ de recherche */}
              <TextField
                type="text"
                placeholder={t("REVISION_GRAPH_SEARCH_DIMENSIONS")}
                value={searchValue}
                onChange={(e) => handleSearch(e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  width: '350px',
                  '& .MuiInputBase-root': {
                    height: '51px',
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searchValue ? (
                        <ButtonIcon onClick={handleClearSearch}>
                          <ClearIcon />
                        </ButtonIcon>
                      ) : (
                        <ButtonIcon disabled>
                          <SearchOutlined />
                        </ButtonIcon>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </Box>
        <ButtonIcon
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </ButtonIcon>
        <DialogContent sx={{
          padding: '0px',
          width: 'auto',
          minWidth: "410px"
        }}>
          <Box sx={{
            backgroundColor: "white",
            width: "100%",
            margin: "0 auto",
            height: "500px",
            overflow: "auto"
          }}>
            {/* Section des dimensions visibles avec drag & drop */}
            {visibleCategories.length > 0 && (
              <>
                <Box sx={{
                  backgroundColor: '#f8f9fa',
                  padding: '8px 16px',
                  borderBottom: '1px solid #D9D9D9',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  <Typography variant="body2" fontWeight="600" color="primary">
                    ✓ {t('REVISION_GRAPH_VISIBLE_DIMENSIONS')} ({visibleCategories.length}/20)
                  </Typography>
                </Box>
                <DragDropContext
                  onDragEnd={onDragEnd}
                  onDragStart={(start) => setDraggingIndex(start.source.index)}
                  onDragUpdate={() => setDraggingIndex(draggingIndex)}
                >
                  <Droppable droppableId="visible-categories">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        <List sx={{ padding: 0 }}>
                          {visibleCategories.map((category, index) => (
                            <Draggable
                              key={category.id}
                              draggableId={category.id.toString()}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <DimensionItem 
                                    category={category} 
                                    index={index} 
                                    isDragging={snapshot.isDragging}
                                    style={{}}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </List>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </>
            )}

            {/* Section des dimensions non-visibles */}
            {nonVisibleCategories.length > 0 && (
              <>
                <Box sx={{
                  backgroundColor: '#f8f9fa',
                  padding: '8px 16px',
                  borderBottom: '1px solid #D9D9D9',
                  position: 'sticky',
                  top: visibleCategories.length > 0 ? 'auto' : 0,
                  zIndex: 1
                }}>
                  <Typography variant="body2" fontWeight="600" color="text.secondary">
                    ○ {t('REVISION_GRAPH_AVAILABLE_DIMENSIONS')} ({nonVisibleCategories.length})
                  </Typography>
                </Box>
                {useVirtualizationForNonVisible ? (
                  <FixedSizeList
                    height={Math.min(400, nonVisibleCategories.length * 58)}
                    width="100%"
                    itemCount={nonVisibleCategories.length}
                    itemSize={58}
                    direction={theme.direction}
                  >
                    {({ index, style }) => {
                      const category = nonVisibleCategories[index];
                      return (
                        <div key={category.id} style={style}>
                          <DimensionItem 
                            category={category} 
                            index={index} 
                            style={{}} 
                          />
                        </div>
                      );
                    }}
                  </FixedSizeList>
                ) : (
                  <List sx={{ padding: 0 }}>
                    {nonVisibleCategories.map((category, index) => (
                      <DimensionItem 
                        key={category.id}
                        category={category} 
                        index={index} 
                        style={{}}
                      />
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
        </DialogContent>
    
        <DialogActions sx={{ display: "flex", justifyContent: "center", height: "91px", borderTop: "solid 1px #D9D9D9",  }}>
       <Box sx={{width: '75%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px'}}>
        <Box >
          <Typography variant='body1' fontWeight='600' fontSize='18px' color='#2766F0'>
          {`${localCategories.filter(cat => cat.visible).length}`}/{availableSelected}  {t('REVISION_GRAPH_DIMENSIONS_SELECTED')} ({`${categories.length- 5}`} {t('REVISION_GRAPH_DIMENSIONS_AVAILABLE_2')})
          </Typography>
          </Box>
          <ButtonCustom
            onClick={handleSave}
            variant="contained"
            size='medium'
            disabled={isUpdating}
            startIcon={isUpdating ? (
              <CircularProgress
                size={20}
                sx={{
                  color: 'white',
                }}
              />
            ) : null}
          >
            {t('SAVE_BUTTON')}
          </ButtonCustom>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};